import { HttpEventType } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  computed,
  input,
  signal,
} from '@angular/core';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  BaseEmailAttachment,
  BasicStatementV2,
  CollectionsApiService,
  DynamicActionInstance,
  EmailActionInstance,
  EmailActionPreview,
  EmailActionSettings,
  EmailAttachmentMetadata,
  NextStatement,
  PreviewAttachmentResponse,
  PreviousStatement,
  Statement,
  StatementAttachmentPayload,
  StatementEmailMetadata,
  StatementPreview,
  StatementPreviewErrorReason,
  StatementPreviewWithMetadata,
  StatementStatus,
  UploadApiService,
  isNextStatement,
  isPreviousStatement,
} from '@anduin/api';
import { FirmPortalPermission, LaunchDarklyFlag, LaunchDarklyState, PermissionsState } from '@anduin/auth';
import {
  DEFAULT_LOADABLE_STATE,
  DEFAULT_SNACKBAR_CONFIG,
  FileSource,
  FileUploadResponse,
  Loadable,
  LoadableHelper,
  LoadableStatus,
  SnackbarComponent,
  SnackbarType,
  arrayBufferToBase64,
  extractFailureContext,
  isEmpty,
  mapToLoadable,
  removeArrayItem,
} from '@anduin/shared';
import { Store } from '@ngxs/store';
import { Observable, Subject, combineLatest, defer, of, throwError } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, take, takeWhile, tap } from 'rxjs/operators';
import {
  Clients_ApproveStatement,
  Clients_SkipStatement,
  Collections_ApproveStatement,
  Collections_SkipStatement,
  CommonCollectionsState,
  GetMessageVariables,
} from '../../state';
import { CollectionsStatementsStep, CollectionsTrackService } from '../../tracking';
import { SmartStatementAction } from '../statement-preview/statement-preview.component';

const STATEMENT_PREVIEW_ERROR_MESSAGES: Record<StatementPreviewErrorReason, string> = {
  [StatementPreviewErrorReason.NO_VALID_EMAIL_RECIPIENT]:
    'There are no valid recipients for this statement. Please ensure your PM System has correct contact information, including who should receive these statements.',
  [StatementPreviewErrorReason.UNKNOWN]: 'There was an issue generating the preview.',
};

@Component({
  selector: 'sequence-item',
  templateUrl: './sequence-item.component.html',
  styleUrls: ['./sequence-item.component.scss'],
  standalone: false,
})
export class SequenceItemComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatExpansionPanel) expansionPanel: MatExpansionPanel;

  isCollectionsV2Enabled = this.store.selectSnapshot(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2));

  // Permissions for statement editing and sending
  canEditAndSendStatements = this.store.selectSignal(
    PermissionsState.hasPermission([FirmPortalPermission.COLLECTIONS_CAN_EDIT_AND_SEND_STATEMENTS]),
  );

  status = signal<StatementStatus>(undefined);
  _action: PreviousStatement | NextStatement;
  attachments = signal<(EmailAttachmentMetadata & { fileId?: number })[]>([]);
  attachmentsToAdd: BaseEmailAttachment[] = [];
  addingAttachmentsComplete$ = new Subject<void>();

  @Input() firstInList = false;
  @Input() implementingModule: 'Collections' | 'Clients';
  @Input() excludedFromCollections = true;

  statement = input<BasicStatementV2>();
  messageVariablesStatus = this.store.selectSignal(CommonCollectionsState.getMessageVariablesStatus);

  isMessageVariablesLoading = computed<boolean>(() => LoadableHelper.isLoading(this.messageVariablesStatus()));
  emailMetadata = signal<Loadable<EmailActionInstance>>(DEFAULT_LOADABLE_STATE);
  isEmailMetadataLoading = computed<boolean>(() => LoadableHelper.isLoading(this.emailMetadata().status));
  statementPreview = signal<Loadable<string>>(DEFAULT_LOADABLE_STATE);
  isStatementPreviewLoading = computed<boolean>(() => LoadableHelper.isLoading(this.statementPreview().status));
  didStatementPreviewError = computed<boolean>(() => this.statementPreview().status === LoadableStatus.ERROR);
  attachmentsToAddV2 = signal<File[]>([]);
  attachmentsToRemoveV2 = signal<PreviewAttachmentResponse[]>([]);
  isUndoProcessing = input<boolean>(false);

  isLoading = computed<boolean>(() => this.isEmailMetadataLoading() || this.isMessageVariablesLoading());

  @Input()
  set action(action: Statement) {
    if (this.isCollectionsV2Enabled) {
      return;
    }

    // If it's a historical action, we can just preview stuff
    if (isPreviousStatement(action)) {
      // Only mark as loaded if we have the html_body, otherwise it will need to be fetched
      if (action.html_body) {
        this.statementPreview.set({ status: LoadableStatus.LOADED, data: action.html_body });
      }
      this.emailMetadata.set({ status: LoadableStatus.LOADED, data: action.action_def_instance_json });
      this._action = action;
    } else if (isNextStatement(action)) {
      // Next/staged action email settings are a little nested but interactions same
      this.emailMetadata.set({ status: LoadableStatus.LOADING, data: action.action.secondaryAction });
      this._action = action;
    }

    this.status.set(action.status);
  }

  previewErrorReason = signal<StatementPreviewErrorReason>(StatementPreviewErrorReason.UNKNOWN);
  previewErrorMessage = computed<string>(() => STATEMENT_PREVIEW_ERROR_MESSAGES[this.previewErrorReason()]);
  noValidEmails = computed<boolean>(() => this.previewErrorReason() === StatementPreviewErrorReason.NO_VALID_EMAIL_RECIPIENT);

  currentActionProcessing = signal<SmartStatementAction>(undefined);

  @Output() undo: EventEmitter<number> = new EventEmitter<number>();

  // Making available to the template
  StatementStatus = StatementStatus;

  private readonly _unsubscribe$ = new Subject<void>();

  constructor(
    private collectionsApiService: CollectionsApiService,
    private store: Store,
    private readonly snackbar: MatSnackBar,
    private readonly uploadApiService: UploadApiService,
    private readonly trackService: CollectionsTrackService,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(new GetMessageVariables());

    if (this.isCollectionsV2Enabled) {
      this.status.set(this.statement().status);
      return;
    }

    if (this.status() === StatementStatus.STAGED) {
      this.setEmailPreview((this._action as NextStatement).action).subscribe();
    }
  }

  ngAfterViewInit(): void {
    if (this.firstInList && this.status() !== StatementStatus.DECLINED) {
      this.expansionPanel.open();
    }
  }

  /**
   * V2
   *
   * Retrieve the details of the statement, which includes the email metadata (from, to, subject, etc.) and the email body.
   * Only include the metadata on the initial load, not on subsequent loads.
   */
  onStatementSelected(overrides?: StatementEmailMetadata, forceRefresh = false): void {
    if (this.isCollectionsV2Enabled) {
      this.handleV2Flow(overrides, forceRefresh);
    } else {
      this.handleV1Flow(forceRefresh);
    }
  }

  /**
   * V2 Flow: Fetches statement preview with optional email metadata
   */
  private handleV2Flow(overrides?: StatementEmailMetadata, forceRefresh = false): void {
    if (!this.shouldFetchData(forceRefresh)) {
      return;
    }

    const isRetrievingEmailMetadata = overrides === undefined;
    const statementId = this.statement().id;
    const startedAt = performance.now();

    if (isRetrievingEmailMetadata) {
      this.emailMetadata.set({ status: LoadableStatus.LOADING });
    }

    this.trackService.trackWorkflow(CollectionsStatementsStep.STATEMENT_LOAD_SUBMITTED, { statementId });

    this.collectionsApiService
      .getStatementPreview(statementId, overrides)
      .pipe(
        mapToLoadable<StatementPreview>(undefined, true),
        filter(({ status }) => status !== LoadableStatus.DEFAULT),
        tap((previewLoadable) => {
          this.updateV2State(previewLoadable, isRetrievingEmailMetadata);
          if (previewLoadable.status === LoadableStatus.LOADED) {
            this.trackService.trackWorkflow(CollectionsStatementsStep.STATEMENT_LOAD_SUCCEEDED, {
              statementId,
              operationDurationMs: performance.now() - startedAt,
            });
          } else if (previewLoadable.status === LoadableStatus.ERROR) {
            this.trackService.trackWorkflow(CollectionsStatementsStep.STATEMENT_LOAD_FAILED, {
              statementId,
              ...extractFailureContext(previewLoadable.error),
              operationDurationMs: performance.now() - startedAt,
            });
          }
        }),
        takeWhile(({ status }) => LoadableHelper.isLoading(status)),
      )
      .subscribe();
  }

  /**
   * V1 Flow: Fetches preview from API using action ID
   */
  private handleV1Flow(forceRefresh: boolean): void {
    if (!this._action) {
      return;
    }

    if (!this.shouldFetchData(forceRefresh)) {
      return;
    }

    this.setLoadingState();

    const statementId = this._action.id;
    const startedAt = performance.now();
    this.trackService.trackWorkflow(CollectionsStatementsStep.STATEMENT_LOAD_SUBMITTED, { statementId });

    this.collectionsApiService
      .getEmailActionPreviewById(this._action.id)
      .pipe(
        tap(({ response }) => {
          this.updateV1State(response);
          this.trackService.trackWorkflow(CollectionsStatementsStep.STATEMENT_LOAD_SUCCEEDED, {
            statementId,
            operationDurationMs: performance.now() - startedAt,
          });
        }),
        catchError((error) => {
          this.trackService.trackWorkflow(CollectionsStatementsStep.STATEMENT_LOAD_FAILED, {
            statementId,
            ...extractFailureContext(error),
            operationDurationMs: performance.now() - startedAt,
          });
          return this.handleV1Error(error);
        }),
        take(1),
      )
      .subscribe();
  }

  /**
   * Determines if data should be fetched based on current state and force refresh flag
   */
  private shouldFetchData(forceRefresh: boolean): boolean {
    return forceRefresh || LoadableHelper.shouldRequest(this.statementPreview().status);
  }

  /**
   * Sets both preview and metadata to loading state
   */
  private setLoadingState(): void {
    this.statementPreview.set({ status: LoadableStatus.LOADING });
    this.emailMetadata.set({ status: LoadableStatus.LOADING });
  }

  /**
   * Updates state for V2 flow with preview data and optional email metadata
   */
  private updateV2State(previewLoadable: Loadable<StatementPreview>, isRetrievingEmailMetadata: boolean): void {
    this.statementPreview.set({
      ...previewLoadable,
      data: previewLoadable.data?.previewValue,
    });

    if (isRetrievingEmailMetadata) {
      const { communicationId, previewValue, ...emailMetadata } = (previewLoadable.data ?? {}) as StatementPreviewWithMetadata;

      this.emailMetadata.set({
        status: LoadableStatus.LOADED,
        data: SequenceItemComponent.convertEmailMetadataToV1(emailMetadata),
      });

      this.attachments.set(this.emailMetadata().data?.attachments ?? []);
    }
  }

  /**
   * Updates state for V1 flow with preview response
   */
  private updateV1State(response: any): void {
    this.statementPreview.set({
      status: LoadableStatus.LOADED,
      data: response,
    });

    if (isPreviousStatement(this._action)) {
      this.emailMetadata.set({
        status: LoadableStatus.LOADED,
        data: this._action.action_def_instance_json,
      });
    } else if (this.emailMetadata().status !== LoadableStatus.LOADED) {
      this.emailMetadata.set({
        status: LoadableStatus.LOADED,
        data: this.emailMetadata().data,
      });
    }
  }

  /**
   * Handles errors during V1 flow data fetching
   */
  private handleV1Error(error: any): Observable<never> {
    this.statementPreview.set({ status: LoadableStatus.ERROR, error });
    this.emailMetadata.set({
      status: LoadableStatus.LOADED,
    });
    console.error('Failed to load statement preview:', error);
    return throwError(() => error);
  }

  /**
   * V1
   *
   * Update email preview with response content.
   * @param action
   */
  setEmailPreview(action: DynamicActionInstance): Observable<EmailActionPreview | number> {
    this.statementPreview.set({ status: LoadableStatus.LOADING });

    const statementId = this._action?.id;
    const startedAt = performance.now();
    this.trackService.trackWorkflow(CollectionsStatementsStep.STATEMENT_LOAD_SUBMITTED, { statementId });

    return this.collectionsApiService.getEmailActionPreview(action).pipe(
      tap(({ emailActionInstance, htmlPreview }) => {
        this.emailMetadata.set({ status: LoadableStatus.LOADED, data: { ...this.emailMetadata().data, ...emailActionInstance } });
        this.statementPreview.set({ status: LoadableStatus.LOADED, data: htmlPreview });
        this.trackService.trackWorkflow(CollectionsStatementsStep.STATEMENT_LOAD_SUCCEEDED, {
          statementId,
          operationDurationMs: performance.now() - startedAt,
        });
      }),
      catchError((error) => {
        const failureContext = {
          statementId,
          ...extractFailureContext(error),
          operationDurationMs: performance.now() - startedAt,
        };

        this.statementPreview.set({ status: LoadableStatus.ERROR, error });

        const clientId = action.valueMap['clientId'];
        return this.collectionsApiService.getValidEmailRecipientsCount(clientId).pipe(
          catchError(() => of(null)),
          tap((count) => {
            const reason =
              count === 0 ? StatementPreviewErrorReason.NO_VALID_EMAIL_RECIPIENT : StatementPreviewErrorReason.UNKNOWN;
            this.previewErrorReason.set(reason);
            this.trackService.trackWorkflow(CollectionsStatementsStep.STATEMENT_LOAD_FAILED, {
              ...failureContext,
              previewErrorReason: reason,
            });
            throw new Error(error);
          }),
        );
      }),
    );
  }

  handleUpdatePreview(action: EmailActionSettings): void {
    this.trackService.trackWorkflow(CollectionsStatementsStep.STATEMENT_EDITED, {
      statementId: this.getStatementId(),
    });

    if (this.isCollectionsV2Enabled) {
      this.emailMetadata.set({ status: LoadableStatus.LOADED, data: { ...this.emailMetadata(), ...action } });
      this.onStatementSelected(SequenceItemComponent.convertEmailMetadataToV2(action), true);
      return;
    }

    action.templateParams = null;
    if (action.to?.length === 0) {
      action.to = null;
    }

    const updatedDynamicAction: DynamicActionInstance = {
      ...(this._action as NextStatement).action,
      secondaryAction: action,
      valueMap: (this._action as NextStatement).action.valueMap,
    };

    this.setEmailPreview(updatedDynamicAction).subscribe();
  }

  handleApprove(action: Partial<EmailActionSettings>): void {
    this.currentActionProcessing.set(SmartStatementAction.SEND);

    const statementId = this.getStatementId();
    this.trackService.trackWorkflow(CollectionsStatementsStep.APPROVE_SUBMITTED, { statementId });
    const startedAt = performance.now();

    this.updateEmailMetadata(action as EmailActionInstance);

    this.addingAttachmentsComplete$
      .pipe(
        take(1),
        switchMap((_) => {
          if (this.isCollectionsV2Enabled) {
            return this.handleAttachmentChangesV2$().pipe(map((_) => this.getApproveActionV2()));
          }

          return defer(() => {
            if (isEmpty(this.attachmentsToAdd)) {
              return of([] as EmailAttachmentMetadata[]);
            }

            const attachmentStartedAt = performance.now();
            this.trackService.trackWorkflow(CollectionsStatementsStep.ATTACHMENT_ADD_SUBMITTED, { statementId });

            return this.collectionsApiService.addEmailAttachments(this._action.id, this.attachmentsToAdd).pipe(
              tap({
                next: (data) => {
                  this.attachmentsToAdd.length = 0;
                  this.attachments.set(this.attachments().concat(data));
                  this.trackService.trackWorkflow(CollectionsStatementsStep.ATTACHMENT_ADD_SUCCEEDED, {
                    statementId,
                    operationDurationMs: performance.now() - attachmentStartedAt,
                  });
                },
                error: (err: unknown) => {
                  this.trackService.trackWorkflow(CollectionsStatementsStep.ATTACHMENT_ADD_FAILED, {
                    statementId,
                    ...extractFailureContext(err),
                    operationDurationMs: performance.now() - attachmentStartedAt,
                  });
                },
              }),
            );
          }).pipe(map((_) => this.getApproveActionV1(action)));
        }),
        switchMap((action) => this.store.dispatch(action)),
        tap({
          next: (_) => {
            this.trackService.trackWorkflow(CollectionsStatementsStep.APPROVE_SUCCEEDED, {
              statementId,
              operationDurationMs: performance.now() - startedAt,
            });
            this.openSnackbar('Statement sent', SnackbarType.SUCCESS);
            this.status.set(StatementStatus.EXECUTED);
            this.expansionPanel.close();
          },
          error: (err: unknown) => {
            this.trackService.trackWorkflow(CollectionsStatementsStep.APPROVE_FAILED, {
              statementId,
              ...extractFailureContext(err),
              operationDurationMs: performance.now() - startedAt,
            });
            this.openSnackbar('Failed to send the statement, please try again later.', SnackbarType.ERROR);
          },
        }),
        take(1),
        finalize(() => this.currentActionProcessing.set(undefined)),
      )
      .subscribe();
  }

  getApproveActionV1(action: Partial<EmailActionSettings>): Clients_ApproveStatement | Collections_ApproveStatement {
    // copy the updated email settings to the dynamic email action
    const updatedDynamicAction: EmailActionSettings = {
      ...(this._action as NextStatement).action.secondaryAction,
      ...action,
      attachments: this.attachments(),
    };

    const actionCopy: typeof this._action = { ...this._action };
    delete actionCopy['client'];

    const nextStatement: NextStatement = {
      ...(actionCopy as NextStatement),
      action: {
        ...(this._action as NextStatement).action,
        secondaryAction: updatedDynamicAction,
      },
    };

    if (!['Clients', 'Collections'].includes(this.implementingModule)) {
      throw new Error('Implementing module not set for approving statement');
    }

    return new (this.implementingModule === 'Clients' ? Clients_ApproveStatement : Collections_ApproveStatement)(
      (this._action as NextStatement).client.id,
      nextStatement,
    );
  }

  getApproveActionV2(): Clients_ApproveStatement | Collections_ApproveStatement {
    const { attachments, ...emailMetadata } = SequenceItemComponent.convertEmailMetadataToV2(this.emailMetadata().data);
    return new (this.implementingModule === 'Clients' ? Clients_ApproveStatement : Collections_ApproveStatement)(
      // just to appease the olden ways (normally just call this selector from within the action itself)
      this.store.selectSnapshot(CommonCollectionsState.getSelectedStatementRecipientId),
      { communicationId: this.statement().id, ...emailMetadata },
    );
  }

  handleSkip(): void {
    this.currentActionProcessing.set(SmartStatementAction.SKIP);

    const { recipientId, actionId } = this.isCollectionsV2Enabled
      ? {
          recipientId: this.store.selectSnapshot(CommonCollectionsState.getSelectedStatementRecipientId),
          actionId: this.statement().id,
        }
      : {
          recipientId: (this._action as NextStatement).client.id,
          actionId: (this._action as Statement).id,
        };

    this.trackService.trackWorkflow(CollectionsStatementsStep.SKIP_SUBMITTED, { statementId: actionId });
    const startedAt = performance.now();

    const action = new (this.implementingModule === 'Clients' ? Clients_SkipStatement : Collections_SkipStatement)(recipientId, actionId);

    this.store
      .dispatch(action)
      .pipe(
        tap({
          next: (_) => {
            this.trackService.trackWorkflow(CollectionsStatementsStep.SKIP_SUCCEEDED, {
              statementId: actionId,
              operationDurationMs: performance.now() - startedAt,
            });
            this.openSnackbar('Statement skipped', SnackbarType.SUCCESS);
            this.expansionPanel.close();
            this.status.set(StatementStatus.DECLINED);
          },
          error: (err: unknown) => {
            this.trackService.trackWorkflow(CollectionsStatementsStep.SKIP_FAILED, {
              statementId: actionId,
              ...extractFailureContext(err),
              operationDurationMs: performance.now() - startedAt,
            });
            this.openSnackbar('Failed to skip the statement, please try again later.', SnackbarType.ERROR);
          },
        }),
        take(1),
        finalize(() => this.currentActionProcessing.set(undefined)),
      )
      .subscribe();
  }

  handleTestEmail(action: EmailActionPreview): void {
    this.currentActionProcessing.set(SmartStatementAction.TEST);

    const statementId = this.getStatementId();
    this.trackService.trackWorkflow(CollectionsStatementsStep.TEST_EMAIL_SUBMITTED, { statementId });
    const startedAt = performance.now();

    this.updateEmailMetadata(action.emailActionInstance);

    this.addingAttachmentsComplete$
      .pipe(
        take(1),
        switchMap((_) => {
          if (this.isCollectionsV2Enabled) {
            return this.handleAttachmentChangesV2$().pipe(
              switchMap((_) => {
                const communicationId = this.statement().id;
                const emailMetadataV2 = SequenceItemComponent.convertEmailMetadataToV2(this.emailMetadata().data);
                const { attachments, toList, ccList, bccList, ...emailMetadata } = emailMetadataV2;

                return this.collectionsApiService.sendTestStatementEmail({ communicationId, ...emailMetadata });
              }),
            );
          }

          const hasV1Attachments = !isEmpty(this.attachmentsToAdd);
          const attachmentStartedAt = performance.now();
          if (hasV1Attachments) {
            this.trackService.trackWorkflow(CollectionsStatementsStep.ATTACHMENT_ADD_SUBMITTED, { statementId });
          }

          return (
            hasV1Attachments
              ? this.collectionsApiService.addEmailAttachments((this._action as Statement).id, this.attachmentsToAdd)
              : of([] as EmailAttachmentMetadata[])
          ).pipe(
            tap({
              next: (data) => {
                this.attachmentsToAdd.length = 0;
                this.attachments.set(this.attachments().concat(data));
                if (hasV1Attachments) {
                  this.trackService.trackWorkflow(CollectionsStatementsStep.ATTACHMENT_ADD_SUCCEEDED, {
                    statementId,
                    operationDurationMs: performance.now() - attachmentStartedAt,
                  });
                }
              },
              error: (err: unknown) => {
                if (hasV1Attachments) {
                  this.trackService.trackWorkflow(CollectionsStatementsStep.ATTACHMENT_ADD_FAILED, {
                    statementId,
                    ...extractFailureContext(err),
                    operationDurationMs: performance.now() - attachmentStartedAt,
                  });
                }
              },
            }),
            switchMap((_) =>
              this.collectionsApiService.sendTestEmailAction({
                emailActionInstance: { ...action.emailActionInstance, attachments: this.attachments() ?? [] },
                htmlPreview: action.htmlPreview,
              }),
            ),
          );
        }),
        tap({
          next: (_) => {
            this.trackService.trackWorkflow(CollectionsStatementsStep.TEST_EMAIL_SUCCEEDED, {
              statementId,
              operationDurationMs: performance.now() - startedAt,
            });
            this.openSnackbar('Test email sent', SnackbarType.SUCCESS);
          },
          error: (error: unknown) => {
            this.trackService.trackWorkflow(CollectionsStatementsStep.TEST_EMAIL_FAILED, {
              statementId,
              ...extractFailureContext(error),
              operationDurationMs: performance.now() - startedAt,
            });
            const message = String((error as { message?: unknown })?.message ?? '');
            this.openSnackbar(
              `${message.includes('storage.googleapis') ? 'Failed to attach file(s) to statement' : 'Failed to send test email'}, please try again later.`,
              SnackbarType.ERROR,
            );
          },
        }),
        take(1),
        finalize(() => this.currentActionProcessing.set(undefined)),
      )
      .subscribe();
  }

  updateEmailMetadata(emailMetadata: EmailActionInstance): void {
    this.emailMetadata.set({
      ...this.emailMetadata(),
      data: {
        ...this.emailMetadata().data,
        ...emailMetadata,
      },
    });
  }

  openSnackbar(message: string, type: SnackbarType): void {
    this.snackbar.openFromComponent(SnackbarComponent, {
      ...DEFAULT_SNACKBAR_CONFIG,
      data: { message, type },
    });
  }

  /**
   * Read files and prepare for storage. Emit signal when all files are loaded.
   * @param files
   */
  handleAttachment(files: File[]): void {
    if (!files.length) {
      this.addingAttachmentsComplete$.next();
      return;
    }

    if (this.isCollectionsV2Enabled) {
      console.log('file added', files);
      this.attachmentsToAddV2.set(this.attachmentsToAddV2().concat(files));
      this.addingAttachmentsComplete$.next();
      return;
    }

    // files.length gets updated before .onload fires, so storing it here
    const filesLength = files.length;

    files.forEach((file) => {
      const reader = new FileReader();
      // read file data as array buffer
      reader.readAsArrayBuffer(file);
      const attach: BaseEmailAttachment = {} as any;
      attach.display = file.name;

      reader.onload = (evt) => {
        attach.data = arrayBufferToBase64(evt.target.result as ArrayBuffer);
        this.attachmentsToAdd = this.attachmentsToAdd.concat(attach);
        if (this.attachmentsToAdd.length === filesLength) {
          this.addingAttachmentsComplete$.next();
        }
      };
    });
  }

  handleAttachmentChangesV2$(): Observable<boolean> {
    if (isEmpty(this.attachmentsToAddV2()) && isEmpty(this.attachmentsToRemoveV2())) {
      return of(true);
    }

    // Single batch POST covers adds + removes; emit ADD/REMOVE SUBMITTED separately so
    // funnels can isolate the two intents even when they share an `operationDurationMs`.
    const statementId = this.statement().id;
    const hasAdds = !isEmpty(this.attachmentsToAddV2());
    const hasRemoves = !isEmpty(this.attachmentsToRemoveV2());
    const attachmentStartedAt = performance.now();

    if (hasAdds) {
      this.trackService.trackWorkflow(CollectionsStatementsStep.ATTACHMENT_ADD_SUBMITTED, { statementId });
    }
    if (hasRemoves) {
      this.trackService.trackWorkflow(CollectionsStatementsStep.ATTACHMENT_REMOVE_SUBMITTED, { statementId });
    }

    const uploadFiles$ = this.attachmentsToAddV2().map((attachment) =>
      this.uploadApiService.fileUpload({ file: attachment, fileSource: FileSource.STATEMENT_ATTACHMENT }),
    );

    return (isEmpty(uploadFiles$) ? of([] as FileUploadResponse[]) : combineLatest(uploadFiles$)).pipe(
      filter((responses) => responses.every(({ httpEvent }) => httpEvent.type === HttpEventType.Response && httpEvent.ok)),
      switchMap((uploadedFiles) => {
        // add
        const filesToAttach: StatementAttachmentPayload[] = uploadedFiles.map((uploadedFile) => ({
          fileId: uploadedFile.fileMutationResponse.fileId,
          fileName: uploadedFile.fileMutationResponse.fileName,
          gcsFilePath: uploadedFile.fileMutationResponse.url,
          attach: true,
        }));

        // remove
        const filesToRemove: StatementAttachmentPayload[] = this.attachmentsToRemoveV2().map(({ fileId }) => ({ fileId, attach: false }));

        // all
        const files = filesToAttach.concat(filesToRemove);

        // send to backend
        return this.collectionsApiService.attachFilesToStatement({ communicationId: this.statement().id, files }).pipe(
          // update local state
          tap((_) => {
            const emailAttachments = filesToAttach.map((attachment) => ({
              gcsFilePath: attachment.gcsFilePath,
              display: attachment.fileName,
            }));
            this.attachments.set(this.attachments().concat(emailAttachments));
            this.attachmentsToAddV2.set([]);
          }),
        );
      }),
      tap({
        next: () => {
          const operationDurationMs = performance.now() - attachmentStartedAt;
          if (hasAdds) {
            this.trackService.trackWorkflow(CollectionsStatementsStep.ATTACHMENT_ADD_SUCCEEDED, {
              statementId,
              operationDurationMs,
            });
          }
          if (hasRemoves) {
            this.trackService.trackWorkflow(CollectionsStatementsStep.ATTACHMENT_REMOVE_SUCCEEDED, {
              statementId,
              operationDurationMs,
            });
          }
        },
        error: (err: unknown) => {
          const operationDurationMs = performance.now() - attachmentStartedAt;
          const failureContext = extractFailureContext(err);
          if (hasAdds) {
            this.trackService.trackWorkflow(CollectionsStatementsStep.ATTACHMENT_ADD_FAILED, {
              statementId,
              ...failureContext,
              operationDurationMs,
            });
          }
          if (hasRemoves) {
            this.trackService.trackWorkflow(CollectionsStatementsStep.ATTACHMENT_REMOVE_FAILED, {
              statementId,
              ...failureContext,
              operationDurationMs,
            });
          }
        },
      }),
    );
  }

  /**
   * Remove file from existing attachments.
   */
  handleRemoveFile({ index, isAttachmentUploaded }: { index: number; isAttachmentUploaded: boolean }): void {
    if (!isAttachmentUploaded) {
      const removedStagedFile = this.attachmentsToAddV2()[index];
      console.log('file to be removed', removedStagedFile, this.attachmentsToAddV2());
      if (removedStagedFile) {
        this.attachmentsToAddV2.set(removeArrayItem(this.attachmentsToAddV2(), index));
      }

      return;
    }

    const removedFile = this.attachments()[index];

    if (this.isCollectionsV2Enabled) {
      this.attachmentsToRemoveV2.set(
        this.attachmentsToRemoveV2().concat({
          fileId: removedFile.fileId,
          gcsFilePath: removedFile.gcsFilePath,
          fileName: removedFile.display,
        }),
      );
    }

    this.attachments.set(removeArrayItem(this.attachments(), index));
  }

  /**
   * Bubble up the undo click to the parent
   */
  handleUndoClick(): void {
    this.undo.emit(this.isCollectionsV2Enabled ? this.statement().id : this._action.id);
  }

  private getStatementId(): number {
    return this.isCollectionsV2Enabled ? this.statement().id : this._action.id;
  }

  static convertEmailMetadataToV1(emailMetadata: StatementEmailMetadata): EmailActionInstance {
    if (isEmpty(emailMetadata)) {
      return undefined;
    }

    return {
      from: emailMetadata.from,
      to: emailMetadata.toList,
      ccList: emailMetadata.ccList,
      bccList: emailMetadata.bccList,
      subject: emailMetadata.subject,
      header: emailMetadata.headline,
      body: emailMetadata.body,
      attachments: (emailMetadata.attachments ?? []).map((attachment) => ({
        fileId: attachment.fileId, // ugly workaround to not lose v2 context
        gcsFilePath: attachment.gcsFilePath,
        display: attachment.fileName,
      })) as EmailAttachmentMetadata[],
    } as EmailActionInstance;
  }

  static convertEmailMetadataToV2(emailMetadata: EmailActionInstance): StatementEmailMetadata {
    return {
      from: emailMetadata.from,
      toList: emailMetadata.to,
      ccList: emailMetadata.ccList,
      bccList: emailMetadata.bccList,
      subject: emailMetadata.subject,
      headline: emailMetadata.header,
      body: emailMetadata.body,
      // ugly workaround to not lose v2 context
      attachments: ((emailMetadata.attachments ?? []) as (EmailAttachmentMetadata & { fileId?: number })[]).map((attachment) => ({
        fileId: attachment.fileId,
        gcsFilePath: attachment.gcsFilePath,
        fileName: attachment.display,
      })),
    } as StatementEmailMetadata;
  }

  /**
   * Clean up subscriptions.
   */
  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
